import React, { useEffect, useState } from "react";
import {
  Popover,
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import useGetAll from "@Hooks/useGetAll";
import usePostItem from "@Hooks/usePostItem";
import { EXPORT_SELECTION_URL } from "@API/APIRoutes";
import { useDispatch } from "react-redux";
import { setSnackbar } from "@Redux/snackBarSlice";

export default function ExportSelectionPopover({
  open,
  anchorReference = "anchorPosition",
  anchorPosition,
  anchorEl,
  onClose,
  onExport,
  columnOptions,
  description,
}) {
  const [selectedExportColumns, setSelectedExportColumns] = useState(
    columnOptions
  );
  const [selectionName, setSelectionName] = useState("");
  const [selectedSavedSelectionName, setSelectedSavedSelectionName] =
    useState("");
  const [savedExportSelections, setSavedExportSelections] = useState([]);
  const [isCreatingNewSelection, setIsCreatingNewSelection] = useState(false);
  const dispatch = useDispatch();
  const { loading: saveExportSelectionLoading, postItem: saveExportSelection } =
    usePostItem();
  const { getAll: getExportSelections } = useGetAll();

  const mapSelectionStringToColumns = (selection = "") =>
    selection
      .split(",")
      .map((column) => column.trim())
      .filter((column) => columnOptions.includes(column));

  const isCompatibleSelection = (selection = "") => {
    const requestedColumns = selection
      .split(",")
      .map((column) => column.trim())
      .filter(Boolean);

    if (requestedColumns.length === 0) return false;

    return requestedColumns.every((column) => columnOptions.includes(column));
  };

  const loadSavedExportSelections = async () => {
    const response = await getExportSelections(
      EXPORT_SELECTION_URL,
      "Failed to get export selections"
    );

    if (!response) return;

    const normalizedSelections = response
      .filter((selection) => isCompatibleSelection(selection.selection))
      .map((selection) => ({
        id: selection.id,
        selectionName: selection.selectionName,
        selection: selection.selection,
        columns: mapSelectionStringToColumns(selection.selection),
      }))
      .sort((first, second) =>
        first.selectionName.localeCompare(second.selectionName)
      );

    setSavedExportSelections(normalizedSelections);
  };

  const handleExportColumnToggle = (column) => {
    setSelectedExportColumns((prev) =>
      prev.includes(column)
        ? prev.filter((item) => item !== column)
        : [...prev, column]
    );
  };

  const handleSelectAllExportColumns = () => {
    setSelectedExportColumns(columnOptions);
  };

  const handleClearExportColumns = () => {
    setSelectedExportColumns([]);
  };

  const handleSavedSelectionChange = (event) => {
    const selectedName = event.target.value;
    setSelectedSavedSelectionName(selectedName);
    setIsCreatingNewSelection(false);

    const matchedSelection = savedExportSelections.find(
      (selection) => selection.selectionName === selectedName
    );

    if (!matchedSelection) return;

    setSelectedExportColumns(matchedSelection.columns);
    setSelectionName(matchedSelection.selectionName);
  };

  const handleSaveExportSelection = async () => {
    const trimmedSelectionName = selectionName.trim();
    const orderedSelectedColumns = columnOptions.filter((column) =>
      selectedExportColumns.includes(column)
    );
    const isDuplicateSelectionName = savedExportSelections.some(
      (selection) =>
        selection.selectionName.trim().toLowerCase() ===
        trimmedSelectionName.toLowerCase()
    );

    if (!trimmedSelectionName) {
      dispatch(
        setSnackbar({
          open: true,
          message: "Selection name is required",
          error: true,
        })
      );
      return;
    }

    if (orderedSelectedColumns.length === 0) {
      dispatch(
        setSnackbar({
          open: true,
          message: "Select at least one column",
          error: true,
        })
      );
      return;
    }

    if (isDuplicateSelectionName) {
      dispatch(
        setSnackbar({
          open: true,
          message: "Selection name must be unique",
          error: true,
        })
      );
      return;
    }

    const payload = {
      selectionName: trimmedSelectionName,
      selection: orderedSelectedColumns.join(","),
    };

    const response = await saveExportSelection(
      EXPORT_SELECTION_URL,
      payload,
      () => {},
      "Export selection saved successfully",
      "Failed to save export selection"
    );

    if (!response) return;

    const updatedSelections = [
      ...savedExportSelections,
      {
        id: response.id,
        selectionName: trimmedSelectionName,
        selection: orderedSelectedColumns.join(","),
        columns: orderedSelectedColumns,
      },
    ].sort((first, second) =>
      first.selectionName.localeCompare(second.selectionName)
    );

    setSavedExportSelections(updatedSelections);
    setSelectedSavedSelectionName(trimmedSelectionName);
    setIsCreatingNewSelection(false);
  };

  useEffect(() => {
    loadSavedExportSelections();
  }, []);

  useEffect(() => {
    const currentColumns = [...selectedExportColumns].sort();
    const matchingSelection = savedExportSelections.find((selection) => {
      const savedColumns = [...selection.columns].sort();
      return (
        currentColumns.length === savedColumns.length &&
        currentColumns.every((column, index) => column === savedColumns[index])
      );
    });

    if (matchingSelection) {
      if (selectedSavedSelectionName !== matchingSelection.selectionName) {
        setSelectedSavedSelectionName(matchingSelection.selectionName);
      }
      return;
    }

    if (selectedSavedSelectionName) {
      setSelectedSavedSelectionName("");
    }
  }, [
    selectedExportColumns,
    selectedSavedSelectionName,
    savedExportSelections,
  ]);

  const handleExport = () => {
    const orderedSelectedColumns = columnOptions.filter((column) =>
      selectedExportColumns.includes(column)
    );
    onExport(orderedSelectedColumns);
  };

  return (
    <Popover
      open={open}
      anchorReference={anchorReference}
      anchorPosition={anchorPosition}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          width: 520,
          maxWidth: "calc(100vw - 32px)",
          p: 2,
          borderRadius: 2,
        },
      }}
    >
      <Box>
        <Typography variant="h6" sx={{ mb: 1, color: "primary.main" }}>
          Export Columns
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          {description}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr auto",
            },
            gap: 1.5,
            mb: 2,
            alignItems: "center",
          }}
        >
          <Select
            size="small"
            displayEmpty
            value={selectedSavedSelectionName}
            onChange={handleSavedSelectionChange}
            fullWidth
          >
            <MenuItem value="" disabled>
              Saved Selections
            </MenuItem>
            {savedExportSelections.map((selection) => (
              <MenuItem
                key={selection.selectionName}
                value={selection.selectionName}
              >
                {selection.selectionName}
              </MenuItem>
            ))}
          </Select>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setIsCreatingNewSelection((prev) => !prev);
              setSelectedSavedSelectionName("");
              setSelectionName("");
            }}
            disabled={
              Boolean(selectedSavedSelectionName) && !isCreatingNewSelection
            }
            sx={{ textTransform: "none", minWidth: 150 }}
          >
            {isCreatingNewSelection ? "Cancel New" : "Add New Selection"}
          </Button>
        </Box>
        {isCreatingNewSelection && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr auto",
              },
              gap: 1.5,
              mb: 2,
              alignItems: "center",
            }}
          >
            <TextField
              size="small"
              label="Selection Name"
              value={selectionName}
              onChange={(event) => setSelectionName(event.target.value)}
              fullWidth
            />
            <Button
              size="small"
              variant="contained"
              onClick={handleSaveExportSelection}
              disabled={saveExportSelectionLoading}
              sx={{ textTransform: "none", minWidth: 120 }}
            >
              Save Selection
            </Button>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleSelectAllExportColumns}
            sx={{ textTransform: "none" }}
          >
            Select All
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleClearExportColumns}
            sx={{ textTransform: "none" }}
          >
            Clear All
          </Button>
        </Box>
        <FormGroup
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
            },
            maxHeight: 420,
            overflowY: "auto",
            columnGap: 2,
            rowGap: 0.5,
            pr: 1,
          }}
        >
          {columnOptions.map((column) => (
            <FormControlLabel
              key={column}
              control={
                <Checkbox
                  checked={selectedExportColumns.includes(column)}
                  onChange={() => handleExportColumnToggle(column)}
                  size="small"
                />
              }
              label={column}
            />
          ))}
        </FormGroup>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={selectedExportColumns.length === 0}
            sx={{ textTransform: "none" }}
          >
            Export
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}
